/**
 * @fileoverview Navigation helpers for department-wide migration report actions.
 * @module system-web/services/legacyMigrationRunNav
 *
 * Responsibilities:
 * - Build router state that re-opens the Occupancies (or other dataset) grid for a department.
 * - Choose a retry selection: stored failed IDs, or all rows when this older run did not store them.
 *
 * Dependencies: none (pure).
 * Architecture: presentation-free helpers used by LegacyMigrationRunDetailPage.
 * Notes: Older department-wide runs counted Occupancies failures without failedRows[].
 */

/**
 * @contract LegacyMigrationGridNavState
 * Purpose: React Router location.state for /legacy-migration/runs.
 * Fields:  tenantId, datasetKey
 * Rules:   Both required so the picker is not left empty.
 * Sync:    Client navigation only.
 */
export interface LegacyMigrationGridNavState {
  tenantId: string;
  datasetKey: string;
}

const SELECTED_TENANT_STORAGE_KEY = 'legacyMigration.selectedTenantId';

/**
 * @reusable
 * Usage: Persist the department so Occupancies grid restores after report navigation.
 * Rules: sessionStorage only; empty tenantId is ignored.
 */
export function persistLegacyMigrationTenantId(tenantId: string | null | undefined): void {
  if (!tenantId) return;
  try {
    sessionStorage.setItem(SELECTED_TENANT_STORAGE_KEY, tenantId);
  } catch {
    /* private mode / SSR */
  }
}

/**
 * @reusable
 * Usage: Restore department when opening Occupancies from the report.
 * Rules: Prefer router state; fall back to sessionStorage.
 */
export function readPersistedLegacyMigrationTenantId(): string | null {
  try {
    return sessionStorage.getItem(SELECTED_TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * @reusable
 * Usage: Open Occupancies grid from the department-wide report.
 * Rules: Always include tenantId; datasetKey must match REGISTERED_ADAPTERS.
 */
export function buildDatasetGridNavState(tenantId: string, datasetKey: string): LegacyMigrationGridNavState {
  persistLegacyMigrationTenantId(tenantId);
  return { tenantId, datasetKey };
}

/**
 * @reusable
 * Usage: Re-run Occupancies (or any step) that completed with row failures.
 * Rules: Prefer stored failedRows IDs. If counts.failed > 0 but IDs were not stored
 *        (older worker), return ['*'] so only that adapter is re-applied — not the
 *        3-hour department-wide DAG.
 */
const DATASET_DISPLAY_NAMES: Record<string, string> = {
  inspectableEntities: 'Occupancies',
  occupancyContacts: 'Contacts',
  buildingInformationalItems: 'Building Intel',
  communicationLogs: 'Communication Logs',
  buildingSystems: 'Systems',
  occupancyChemicals: 'Chemicals',
  occupancyTanks: 'Tanks',
  occupancyPermits: 'Permits',
  hydrantMain: 'Hydrants',
  crrProfile: 'CRR',
  crrDetector: 'CRR Detectors',
  crrDetectorEvent: 'CRR Detector Events',
  fileMetadata: 'Documents',
  referenceLookups: 'Reference Lookups',
  udfBlueprints: 'UDF Blueprints',
};

/**
 * @reusable
 * Usage: Re-run report heading (Hydrants, Occupancies, …).
 * Rules: Falls back to the adapter key.
 */
export function displayNameForDatasetKey(datasetKey: string | undefined): string {
  if (!datasetKey) return 'Dataset';
  return DATASET_DISPLAY_NAMES[datasetKey] ?? datasetKey;
}

export function resolveFailedStepRetrySelection(step: {
  failedRows?: Array<{ legacyId: string }>;
  skippedRows?: Array<{ legacyId: string }>;
  counts?: { failed?: number; skipped?: number };
}): { selection: string[]; usesAllRowsFallback: boolean; hasFailed: boolean; hasSkipped: boolean } | null {
  const failedIds = (step.failedRows ?? []).map((row) => row.legacyId).filter(Boolean);
  const skippedIds = (step.skippedRows ?? []).map((row) => row.legacyId).filter(Boolean);
  const ids = [...failedIds, ...skippedIds];
  const hasFailed = (step.counts?.failed ?? 0) > 0 || failedIds.length > 0;
  const hasSkipped = (step.counts?.skipped ?? 0) > 0 || skippedIds.length > 0;
  const sampleIncomplete = (step.counts?.failed ?? 0) > failedIds.length
    || (step.counts?.skipped ?? 0) > skippedIds.length;
  if ((hasFailed || hasSkipped) && sampleIncomplete) {
    return { selection: ['*'], usesAllRowsFallback: true, hasFailed, hasSkipped };
  }
  if (ids.length > 0) {
    return { selection: ids, usesAllRowsFallback: false, hasFailed, hasSkipped };
  }
  if (hasFailed || hasSkipped) {
    return { selection: ['*'], usesAllRowsFallback: true, hasFailed, hasSkipped };
  }
  return null;
}
